import React, { useCallback, useMemo, useState } from 'react';
import { Box, Grid, SearchInput, SelectDropdown, useDebounce, VirtualTable } from '@common-fe/common-fe';

import { FilterSetters } from '@/utils/hooks/useFilters';

import {
  ACTIVITY_DATE_RANGE_FOR_OPTIONS,
  ReportTab,
  ReportTableBaseProps,
  ReportTypeKeys,
} from '../../report.types';
import { AddScheduledReportButton } from '../../ScheduledReport';
import EmployerSelector from '../EmployerSelector';
import ExportButton from '../ExportButton';
import {
  useIsEmployerIsPresent,
  useMultipleEmployerSelectAvailable,
  useShowAppliedSettingsSnackbar,
} from '../hooks';
import MultipleEmployerSelector from '../MultipleEmployerSelector';
import PlaceholderBase from '../PlaceholderBase';
import ReportLastUpdateDate from '../ReportLastUpdateDate';
import ReportTableWrapper from '../ReportTable/ReportTableWrapper';
import ToggleSSNVisible from '../ToggleSSNVisible';
import TopButtonPortalWrapper from '../TopButtonPortalWrapper';

import ClaimTypeSelector from './ClaimTypeSelector/ClaimTypeSelector';
import ClaimReportDateRange from './ClaimReportDateRange';
import { ClaimsActivityReportTableParams } from './ClaimsActivityReport.types';
import { useClaimsActivityReportExportCSVQuery, useClaimsActivityReportQuery } from './queries';

interface Props extends ReportTableBaseProps {
  filtersValues: ClaimsActivityReportTableParams;
  onSetFilterValue: FilterSetters<ClaimsActivityReportTableParams>;
}

const ClaimsActivityReportTable: React.FC<Props> = ({
  filtersValues,
  onSetFilterValue,
  isPreviewMode,
  defaultEmployerId,
}) => {
  const isEmployerIsPresent = useIsEmployerIsPresent(filtersValues, {
    isMultipleEmployerMode: true,
  });
  const isMultipleEmployerAvailable = useMultipleEmployerSelectAvailable();
  const [isAutoFocus, setAutoFocus] = useState(false);
  const debouncedSearch = useDebounce(filtersValues.searchString);
  const {
    isHasNextPage,
    total,
    isLoading,
    headers,
    nodes,
    lastTimeUpdateDate,
    columnWidths,
    isExportDisabled,
    handleLoadMore,
    onChangeHeaderSize,
  } = useClaimsActivityReportQuery({
    ...filtersValues,
    searchString: debouncedSearch,
    isPreviewMode,
    isMultipleEmployer: isMultipleEmployerAvailable,
  });
  const childrenNode = useMemo(() => {
    if (!isEmployerIsPresent) {
      return (
        <PlaceholderBase>
          <PlaceholderBase.Link onClick={() => setAutoFocus(true)} />
        </PlaceholderBase>
      );
    }

    if (!isLoading && !total) {
      if (filtersValues.searchString.trim()) {
        return (
          <PlaceholderBase isLarge>
            <PlaceholderBase.Search />
          </PlaceholderBase>
        );
      }
      return (
        <PlaceholderBase isLarge>
          <PlaceholderBase.Default />
        </PlaceholderBase>
      );
    }
    return (
      <VirtualTable
        testId="report-table"
        height={isPreviewMode ? 'calc(100vh - 545px)' : 'calc(80vh - 160px)'}
        isLoading={isLoading}
        onResizeHeaders={onChangeHeaderSize}
        onLoadMore={handleLoadMore}
        isHasMore={isHasNextPage}
        columnSizes={columnWidths}
        isIndexMode={true}
        headers={headers}
        items={nodes}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isPreviewMode,
    columnWidths,
    filtersValues.employerIds,
    filtersValues.employerId,
    filtersValues.searchString,
    handleLoadMore,
    headers,
    isHasNextPage,
    isLoading,
    nodes,
    onChangeHeaderSize,
    total,
  ]);
  const { handleExport: handleExportCSV, isLoading: isExportLoading } =
    useClaimsActivityReportExportCSVQuery(filtersValues);

  const handleShowAppliedFilterSnackbar = useShowAppliedSettingsSnackbar();
  const handleToggleSSN = useCallback(
    (value: boolean) => {
      onSetFilterValue.isSSNShowed(value);
      handleShowAppliedFilterSnackbar();
    },
    [handleShowAppliedFilterSnackbar, onSetFilterValue]
  );
  return (
    <Box direction="column">
      <TopButtonPortalWrapper
        isPreviewMode={isPreviewMode}
        infoNode={
          <ReportLastUpdateDate
            isWideMode
            isLoading={isLoading}
            key={`${filtersValues.employerId}`}
            value={lastTimeUpdateDate}
          />
        }
        buttonNode={
          <Box direction="row">
            <AddScheduledReportButton
              employerId={filtersValues.employerId}
              employerIds={filtersValues.employerIds}
              isMultipleEmployer={isMultipleEmployerAvailable}
              type={ReportTypeKeys.ClaimsActivity}
              tab={ReportTab.Report}
            />
            <Box margin={{ left: 'spacing12' }}>
              <ExportButton
                isLoading={isExportLoading}
                onClick={handleExportCSV}
                disabled={!isEmployerIsPresent || isExportDisabled}
                text="Download Report (CSV)"
              />
            </Box>
          </Box>
        }
      >
        <ReportTableWrapper
          total={total}
          isLoading={isLoading}
          isPreviewMode={isPreviewMode}
          headerChildren={
            <ToggleSSNVisible
              disabled={!isEmployerIsPresent}
              value={filtersValues.isSSNShowed}
              onChangeValue={handleToggleSSN}
            />
          }
          isSmallFilterMode
          filters={
            <Box direction="row" fill>
              <Grid
                columns={
                  isPreviewMode
                    ? {
                        count: 3,
                        size: 'auto',
                      }
                    : ['220px', '220px', '280px', '220px', '220px']
                }
                gap="spacing12"
              >
                {!isPreviewMode && (
                  <SearchInput
                    testId="report"
                    placeholder="Search"
                    value={filtersValues.searchString}
                    disabled={!isEmployerIsPresent}
                    onChange={onSetFilterValue.searchString}
                  />
                )}
                {isMultipleEmployerAvailable ? (
                  <MultipleEmployerSelector
                    disabled={isPreviewMode && !!defaultEmployerId}
                    onSetAutoFocus={setAutoFocus}
                    isAutoFocus={isAutoFocus}
                    values={filtersValues.employerIds}
                    onChangeValues={onSetFilterValue.employerIds}
                  />
                ) : (
                  <EmployerSelector
                    disabled={isPreviewMode && !!defaultEmployerId}
                    onSetAutoFocus={setAutoFocus}
                    isAutoFocus={isAutoFocus}
                    value={filtersValues.employerId}
                    onChangeValue={onSetFilterValue.employerId}
                  />
                )}
                {isPreviewMode ? (
                  <SelectDropdown
                    ellipsisMode
                    testId="date-range-for"
                    disabled={!isEmployerIsPresent}
                    activeTitle
                    singleMode
                    placeholder="Select"
                    options={ACTIVITY_DATE_RANGE_FOR_OPTIONS}
                    value={filtersValues.activityDateRangeFor}
                    onChange={onSetFilterValue.activityDateRangeFor}
                    prefix="Date range for"
                    id="date-range-for"
                    name="date-range-for"
                  />
                ) : (
                  <ClaimReportDateRange
                    isClaimSubmitMode={filtersValues.isClaimSubmitMode}
                    onChangeClaimSubmitMode={onSetFilterValue.isClaimSubmitMode}
                    disabled={!isEmployerIsPresent}
                    startDate={filtersValues.startDate}
                    endDate={filtersValues.endDate}
                    onSetStartDate={onSetFilterValue.startDate}
                    onSetEndDate={onSetFilterValue.endDate}
                  />
                )}

                <ClaimTypeSelector
                  disabled={!isEmployerIsPresent}
                  value={filtersValues.claimTypes}
                  onChangeValue={onSetFilterValue.claimTypes}
                />
              </Grid>
            </Box>
          }
        >
          <Box>{childrenNode}</Box>
        </ReportTableWrapper>
      </TopButtonPortalWrapper>
    </Box>
  );
};
export default ClaimsActivityReportTable;
